/**
 * @file SearchableDropdown_style_b_v2.js
 * @description This file contains the SearchableDropdown component with sorted selected items appearing first on open.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useWindowListener } from './useWindowListener';
import '../helpers/SearchableDropdown.css';
import LoadingWrapper from '../ui/loaders/LoadingWrapper';
import styles from './SearchableDropdown.module.scss';
import { getText } from '../../i18n';

export function SearchableDropdown({
    id,
    options,
    optionIDField = 'id',
    updateOptions,
    savedOptions = [],
    savedOptionIDField = 'id',
    disabled = false,
    className,
    showSelectionBox = true,
    showSelectedItemsInField = true,
    resetTrigger,
    isLoading = false,
    showSelectedCount = false,
    showTruncatedNames = false,
    maxLabelCharacters = 30,
    allSelectedLabel = '',
    placeholder = 'Select options',
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOptions, setSelectedOptions] = useState(
        savedOptions.map(option => option && option[savedOptionIDField]),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [sortedOptions, setSortedOptions] = useState([]);
    const componentRef = useRef(null);

    useEffect(() => {
        const selectedOptionsData = selectedOptions.map(optionID =>
            options.find(option => option[optionIDField] === optionID),
        );
        updateOptions(selectedOptionsData);
    }, [selectedOptions, options, optionIDField]);

    useWindowListener('mousedown', event => {
        if (
            componentRef.current &&
            !componentRef.current.contains(event.target)
        ) {
            handleCloseDropdown();
        }
    });

    const sortOptionsBySelection = (optionsToSort, selectedOptions) => {
        return [...optionsToSort].sort((a, b) => {
            const aSelected = selectedOptions.includes(a[optionIDField]);
            const bSelected = selectedOptions.includes(b[optionIDField]);

            if (aSelected === bSelected) {
                return a.name.localeCompare(b.name);
            }

            return aSelected ? -1 : 1;
        });
    };

    const handleCloseDropdown = () => {
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleDropdownToggle = () => {
        if (!disabled) {
            if (!isOpen) {
                setSortedOptions(
                    sortOptionsBySelection(options, selectedOptions),
                );
                setSearchTerm('');
            } else {
                handleCloseDropdown();
            }
            setIsOpen(!isOpen);
        }
    };

    const getFilteredOptions = () => {
        return sortedOptions.filter(option =>
            option.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    };

    const handleSearchChange = event => {
        setSearchTerm(event.target.value);
    };

    const handleOptionToggle = optionID => {
        setSelectedOptions(prevSelectedOptions => {
            if (prevSelectedOptions.includes(optionID)) {
                return prevSelectedOptions.filter(id => id !== optionID);
            }
            return [...prevSelectedOptions, optionID];
        });
    };

    const handleSelectAll = () => {
        if (selectedOptions.length === options.length) {
            setSelectedOptions([]);
        } else {
            setSelectedOptions(options.map(option => option[optionIDField]));
        }
    };

    const handleRemoveOption = optionID => {
        setSelectedOptions(prevSelectedOptions =>
            prevSelectedOptions.filter(id => id !== optionID),
        );
    };

    const handleClearAll = event => {
        event.stopPropagation();
        setSelectedOptions([]);
    };

    const showClearAll =
        selectedOptions.length > 0 && !disabled && !showSelectionBox;

    const selectedOptionNames = selectedOptions
        .map(
            optionID =>
                options.find(option => option[optionIDField] === optionID)
                    ?.name,
        )
        .filter(Boolean);

    const getTruncatedNamesLabel = () => {
        if (selectedOptionNames.length === 0) return placeholder;
        if (disabled) return '-';
        if (selectedOptionNames.length === options.length && allSelectedLabel) {
            return `All ${allSelectedLabel}`;
        }

        let label = selectedOptionNames[0];
        let shown = 1;

        for (let i = 1; i < selectedOptionNames.length; i++) {
            const remaining = selectedOptionNames.length - shown;
            const suffix = ` +${remaining} more`;
            const candidate = `${label}, ${selectedOptionNames[i]}`;

            if (
                candidate.length +
                    (i < selectedOptionNames.length - 1
                        ? ` +${selectedOptionNames.length - shown - 1} more`
                              .length
                        : 0) >
                maxLabelCharacters
            ) {
                return `${label}${suffix}`;
            }
            label = candidate;
            shown++;
        }
        return label;
    };

    const getLabelText = () => {
        if (showTruncatedNames) {
            return getTruncatedNamesLabel();
        }
        if (showSelectedCount) {
            if (selectedOptionNames.length === 0) {
                return `${placeholder}`;
            }
            if (disabled) {
                return '-';
            }
            if (selectedOptionNames.length === options.length) {
                return allSelectedLabel
                    ? `All ${allSelectedLabel}`
                    : `All (${options.length}) Selected`;
            }
            return `${selectedOptionNames.length} Selected`;
        }
        if (
            selectedOptionNames.length > 0 &&
            !showSelectionBox &&
            showSelectedItemsInField
        ) {
            return selectedOptionNames.join(', ');
        }
        return `${placeholder}`;
    };

    const renderDropdownContent = () => {
        const filteredOptions = getFilteredOptions();

        return (
            <ul id={`items-${id}`} className={`${styles.items} items`}>
                <div
                    className={`${styles.dropdownSearchBar} dropdown-search-bar`}
                >
                    <i className={`${styles.searchIcon} bi bi-search`} />
                    <input
                        className={styles.searchInput}
                        onChange={handleSearchChange}
                        value={searchTerm}
                        type="text"
                        placeholder={getText('msg_5616c8722256')}
                        disabled={disabled}
                    />
                </div>
                <hr className={styles.separator} />
                <div className={styles.itemList}>
                    <div className="dropdown-checkbox-container">
                        <input
                            onChange={handleSelectAll}
                            type="checkbox"
                            className="form-check-input custom-checkbox"
                            checked={selectedOptions.length === options.length}
                            disabled={disabled}
                        />
                        <span>{getText('msg_86a599ef4df2')}</span>
                    </div>
                    {filteredOptions.map(option => (
                        <div
                            key={option[optionIDField]}
                            className="dropdown-checkbox-container"
                        >
                            <input
                                onChange={() =>
                                    handleOptionToggle(option[optionIDField])
                                }
                                type="checkbox"
                                className="form-check-input custom-checkbox"
                                checked={selectedOptions.includes(
                                    option[optionIDField],
                                )}
                                disabled={disabled}
                            />
                            <span>{option.name}</span>
                        </div>
                    ))}
                </div>
            </ul>
        );
    };

    return (
        <div className={`${className}`}>
            <LoadingWrapper isLoading={isLoading}>
                <div
                    ref={componentRef}
                    id={`dropdown-${id}`}
                    className={`${styles.dropdownCheckList} dropdown-check-list`}
                >
                    <div
                        id={`label-${id}`}
                        className={`${styles.dropdownCheckListLabel} dropdown-check-list-label ${
                            disabled ? styles.disabled : ''
                        }`}
                        onClick={handleDropdownToggle}
                    >
                        {getLabelText()}
                        <span className={styles.labelActions}>
                            {showClearAll && (
                                <button
                                    type="button"
                                    className={styles.clearButton}
                                    onClick={handleClearAll}
                                    aria-label={getText('msg_247fd63183c7')}
                                    title={getText('msg_247fd63183c7')}
                                >
                                    <i className="bi bi-x-lg" />
                                </button>
                            )}
                            <i
                                className={`${styles.dropdownIcon} bi bi-chevron-${
                                    isOpen ? 'up' : 'down'
                                }`}
                            ></i>
                        </span>
                    </div>
                    {isOpen && renderDropdownContent()}
                </div>
            </LoadingWrapper>
            {showSelectionBox && (
                <div className={styles.selectionBox}>
                    <div
                        className={`${styles.selectionBoxContent} ${
                            disabled ? styles.disabled : ''
                        }`}
                        placeholder={getText('msg_79dd2019667a')}
                        aria-label={getText('msg_82410b800401')}
                    >
                        {selectedOptionNames.map(optionName => (
                            <div
                                key={optionName}
                                className={styles.selectedOption}
                            >
                                <span className="badge-base badge-population-group">
                                    {!disabled && (
                                        <i
                                            onClick={() =>
                                                handleRemoveOption(
                                                    options.find(
                                                        option =>
                                                            option.name ===
                                                            optionName,
                                                    )[optionIDField],
                                                )
                                            }
                                            className={`${styles.removeIcon} bi bi-x-lg`}
                                        ></i>
                                    )}
                                    {optionName}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
